// API service for eCombine Web Dashboard
import { isDemoMode } from '../demo/demoMode'
import { resolveDemoRequest } from '../demo/demoData'

const API_BASE_URL = '/api'

/**
 * Routes that work without an account (see the unauthenticated branch of
 * `App.tsx`). A 401 on one of these is not a reason to leave the page.
 */
const PUBLIC_PATHS = [/^\/athletes\/[^/]+\/profile\/?$/]

export function isPublicPath(pathname: string): boolean {
  return PUBLIC_PATHS.some(pattern => pattern.test(pathname))
}

export interface ApiResponse<T> {
  data?: T
  error?: string
  message?: string
  /**
   * HTTP status for a failed request. Callers need this to tell an *authorized*
   * refusal (403 — "not your event", a legitimate reason to omit a row) from a
   * transport or capacity failure (429, 5xx) that must never be silently
   * dropped. Absent on success and on network errors, which have no status.
   */
  status?: number
}

export interface User {
  id: string
  email: string
  role: 'ATHLETE' | 'COACH' | 'EVENT_ORGANIZER' | 'PARENT' | 'SCOUT' | 'ADMIN'
  profile?: {
    id: string
    firstName: string
    lastName: string
    dateOfBirth?: string
    phoneNumber?: string
  }
  createdAt: string
  updatedAt: string
}

export interface AuthResponse {
  user: User
  token: string
  refreshToken: string
}

export interface Event {
  id: string
  name: string
  description?: string
  sport: string
  startDate: string
  endDate: string
  location: string
  venue?: string
  // The backend's EventStatus enum (prisma/schema.prisma). Until 2026-10-23
  // this said PUBLISHED / IN_PROGRESS, which the API has never accepted: every
  // Publish / Start Event click and the station-operator event list got a 400.
  status: 'DRAFT' | 'SCHEDULED' | 'ACTIVE' | 'COMPLETED' | 'CANCELLED'
  maxParticipants?: number
  registrationDeadline?: string
  participantCount: number
  district?: {
    id: string
    name: string
  }
  school?: {
    id: string
    name: string
  }
  program?: {
    id: string
    name: string
    sport: string
  }
  drills?: Array<{
    id: string
    name: string
    description?: string
    unit: string
    sport: string
  }>
  participants?: Array<{
    id: string
    status: string
    registeredAt: string
    checkedInAt?: string
    athlete: {
      id: string
      profile: {
        firstName: string
        lastName: string
      }
    }
  }>
}

export interface EventsResponse {
  events: Event[]
  pagination: {
    page: number
    limit: number
    total: number
    pages: number
  }
}

export interface District {
  id: string
  name: string
  code: string
  type: string
  city?: string
  state?: string
  superintendent?: string
  phone?: string
  schools: School[]
}

export interface School {
  id: string
  name: string
  code: string
  type: string
  enrollment?: number
  gradeRange?: string
  principal?: string
  programs?: Program[]
  feederSchools: Array<{
    id: string
    name: string
    feedingLevel?: string
  }>
}

export interface Program {
  id: string
  name: string
  sport: string
  level: string
  isActive: boolean
  headCoach?: {
    id: string
    profile: {
      firstName: string
      lastName: string
    }
  }
  teams?: Team[]
}

export interface Team {
  id: string
  name: string
  level: string
  isActive: boolean
  maxPlayers?: number
  playerCount: number
  program: {
    id: string
    name: string
    sport: string
    school: {
      id: string
      name: string
    }
  }
}

export interface OrganizationHierarchy {
  districts: District[]
}

export interface OrganizationNode {
  id: string
  name: string
  type: 'district' | 'school' | 'program'
  level: number
  parentId?: string
  hasChildren: boolean
  userRole?: string
  metadata: {
    enrollment?: number
    gradeRange?: string
    sport?: string
    isActive?: boolean
  }
}

export interface NavigationResponse {
  navigationTree: OrganizationNode[]
}

/**
 * `GET /api/analytics/athletes/:athleteId`, mirroring the route's declared
 * response schema in backend/src/routes/analytics.ts. Optional members are
 * optional in the schema too — Fastify strips undeclared keys, so anything not
 * listed there never arrives, and the UI must handle absence rather than
 * printing `undefined`.
 */
export interface AthleteAnalytics {
  athlete: {
    id: string
    profile: {
      firstName: string
      lastName: string
      position?: string
      dateOfBirth?: string
    }
  }
  performanceSummary: {
    totalEvents: number
    totalResults: number
    averageValue: number
    bestValue: number
    improvementTrend: number
  }
  drillPerformance: Array<{
    drill: {
      id: string
      name: string
      unit: string
    }
    results: Array<{
      value: number
      percentile?: number
      eventName: string
      date: string
    }>
    personalBest: number
    averageValue: number
    trend: number
  }>
  positionRanking?: {
    position: string
    rank: number
    totalAthletes: number
    percentile: number
  }
}

/** `GET /api/athletes/me` — see the `/me` route in backend/src/routes/athletes.ts. */
export interface MyAthleteResponse {
  id: string
  userId: string
  sport: string
  position: string
  graduationYear?: number
  school: string | null
  profile: { firstName: string; lastName: string }
  metrics: { height?: number; weight?: number }
}

/** `GET /api/drills` — see `DrillResponseSchema` in backend/src/routes/drills.ts. */
export interface DrillListResponse {
  drills: Array<{
    id: string
    name: string
    sport: string
    category: string
    description: string
    instructions: string
    measurementType: string
    unit: string
    equipment: string
    videoUrl: string | null
    isActive: boolean
    createdAt: string
    updatedAt: string
  }>
  pagination: { page: number; limit: number; total: number; pages: number }
}

/**
 * `GET /api/profile/:athleteId` — backend/src/routes/profile.ts.
 *
 * The route wraps everything in a `publicData` envelope (plus `gatedData` for
 * an authorized caller). It does NOT return a flat profile: reading
 * `response.data.firstName` yields `undefined`, which is what the recruiting
 * profile page used to do.
 *
 * Note `combineStats` is keyed by drill NAME and carries no `unit` — the route
 * does not select one, so a unit cannot be displayed from this payload.
 */
export interface PublicProfileResponse {
  publicData: {
    firstName: string
    lastName: string
    school: string | null
    graduationYear: number | null
    /** Inches. */
    height: number | null
    /** Pounds. */
    weight: number | null
    positions: Array<{ sport: string; position: string; isPrimary: boolean }>
    sport: string | null
    combineStats: Record<string, { bestResult: number; percentile: number | null }>
    eventCount: number
  }
  gatedData?: Record<string, unknown>
}

/**
 * `GET /api/profile/:athleteId/stats` — backend/src/routes/profile.ts.
 *
 * Category-level rollups, NOT the flat `{ eventsAttended, overallPercentile,
 * personalRecords, combineResults }` the page used to assume. None of those
 * four fields has ever existed on this response.
 */
export interface ProfileStatsResponse {
  athleteId: string
  stats: Array<{
    category: string
    avgPercentile: number
    drills: Array<{ name: string; bestValue: number; percentile: number | null }>
  }>
}

/** `GET /api/monitoring/health` — see `HealthStatusSchema` in backend/src/routes/monitoring.ts. */
export interface SystemHealth {
  status: string
  timestamp: string
  uptime: number
  services: {
    database: string
    redis: string
    api: string
  }
  system: {
    memory: { used: number; total: number; percentage: number }
    cpu: { usage: number }
  }
  errors: {
    total: number
    recent: number
  }
}

export interface EventAnalytics {
  event: {
    id: string
    name: string
    startDate: string
    location: string
    status: string
  }
  participation: {
    registered: number
    checkedIn: number
    completed: number
    noShow: number
    completionRate: number
  }
  drillStats: Array<{
    drill: {
      id: string
      name: string
      unit: string
    }
    totalResults: number
    averageValue: number
    bestValue: number
    medianValue: number
    standardDeviation: number
  }>
  positionBreakdown: Array<{
    position: string
    count: number
    percentage: number
  }>
  schoolBreakdown?: Array<{
    school: {
      id: string
      name: string
    }
    participantCount: number
    // `averagePerformance` removed (#159): the backend always sent exactly 50.
  }>
}

class ApiService {
  private static instance: ApiService
  private token: string | null = null
  private refreshToken: string | null = null
  private refreshInFlight: Promise<boolean> | null = null

  private constructor() {
    // Load tokens from localStorage on initialization
    this.token = localStorage.getItem('auth_token')
    this.refreshToken = localStorage.getItem('auth_refresh_token')
  }

  public static getInstance(): ApiService {
    if (!ApiService.instance) {
      ApiService.instance = new ApiService()
    }
    return ApiService.instance
  }

  public setToken(token: string): void {
    this.token = token
    localStorage.setItem('auth_token', token)
  }

  public getToken(): string | null {
    return this.token
  }

  /**
   * Keep the refresh token login issues. Access tokens live 15 minutes; until
   * this existed the dashboard discarded the refresh token at login and never
   * called /auth/refresh, so every session ended at the 15-minute mark with a
   * bounce to /login, mid-task.
   */
  public setRefreshToken(refreshToken: string): void {
    this.refreshToken = refreshToken
    localStorage.setItem('auth_refresh_token', refreshToken)
  }

  public clearToken(): void {
    this.token = null
    this.refreshToken = null
    localStorage.removeItem('auth_token')
    localStorage.removeItem('auth_refresh_token')
    // Also drop the persisted user so a 401 (e.g. expired token) doesn't leave
    // stale PII in storage between the failed request and the next page load.
    localStorage.removeItem('ecombine_user')
  }

  /**
   * Exchange the refresh token for a new pair. Single-flight: a page that fires
   * several requests when the access token lapses refreshes once, not once per
   * request (each refresh rotates the refresh token, so racing them would
   * invalidate all but one).
   */
  private refreshSession(): Promise<boolean> {
    if (!this.refreshToken) {
      return Promise.resolve(false)
    }
    if (!this.refreshInFlight) {
      const refreshToken = this.refreshToken
      this.refreshInFlight = fetch(`${API_BASE_URL}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      })
        .then(async response => {
          if (!response.ok) {
            return false
          }
          const data = (await response.json()) as { token?: string; refreshToken?: string }
          if (!data.token || !data.refreshToken) {
            return false
          }
          this.setToken(data.token)
          this.setRefreshToken(data.refreshToken)
          return true
        })
        .catch(() => false)
        .finally(() => {
          this.refreshInFlight = null
        })
    }
    return this.refreshInFlight
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    allowRefresh = true
  ): Promise<ApiResponse<T>> {
    // Demo mode: resolve from in-memory fixtures, never touch the network.
    if (isDemoMode()) {
      const method = (options.method || 'GET').toString()
      let body: unknown
      if (typeof options.body === 'string') {
        try {
          body = JSON.parse(options.body)
        } catch {
          body = options.body
        }
      }
      const resolved = resolveDemoRequest(endpoint, method, body)
      // Small delay so loading states render realistically in the demo.
      await new Promise(resolve => setTimeout(resolve, 180))

      // An endpoint with no fixture returns a `{ __demoUnhandled: true }`
      // sentinel. Returning it as `data` made it indistinguishable from a real
      // payload — a truthy object every caller would happily destructure, which
      // is a crash for anything reading a nested field. Surface it as an error
      // so demo mode fails the same honest way live mode does.
      if (
        resolved &&
        typeof resolved === 'object' &&
        (resolved as { __demoUnhandled?: boolean }).__demoUnhandled
      ) {
        return {
          error: 'Not Implemented',
          message: `No demo fixture for ${method} ${endpoint}`,
          status: 501,
        }
      }

      return { data: resolved as T }
    }

    try {
      const config: RequestInit = {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          ...(this.token && { Authorization: `Bearer ${this.token}` }),
          ...options.headers,
        },
      }

      const response = await fetch(`${API_BASE_URL}${endpoint}`, config)

      const data = await response.json()

      // An expired access token is the normal 15-minute case: refresh once
      // and replay the request before treating the session as over. Never for
      // the auth endpoints themselves (a failed login is not an expiry).
      if (
        response.status === 401 &&
        allowRefresh &&
        this.token &&
        !endpoint.startsWith('/auth/') &&
        (await this.refreshSession())
      ) {
        return this.request<T>(endpoint, options, false)
      }

      if (response.status === 401) {
        // Token expired, invalid or absent: clear it. Then go to /login —
        // except from a page that is public by design (#166). The recruiting
        // profile is meant for a college coach with no account, and an
        // unrelated 401 during its bootstrap (the clips call needs a session)
        // used to yank every signed-out visitor to the login screen.
        //
        // Deliberately keyed on the page, not on whether a token was sent: a
        // signed-in user whose stale token is cleared on a public page must
        // still be sent to /login by the next 401 elsewhere in the app.
        this.clearToken()
        const path = window.location.pathname
        if (!path.includes('/login') && !isPublicPath(path)) {
          window.location.href = '/login'
        }
        return {
          error: data.error || 'Unauthorized',
          message: data.message || 'Authentication failed',
          status: response.status,
        }
      }

      if (!response.ok) {
        return {
          error: data.error || 'Request failed',
          message: data.message || 'An error occurred',
          status: response.status,
        }
      }

      return { data }
    } catch (error) {
      console.error('API Request Error:', error)
      return {
        error: 'Network Error',
        message: 'Unable to connect to server. Please check your connection.',
      }
    }
  }

  // Authentication APIs
  public async login(credentials: {
    email: string
    password: string
  }): Promise<ApiResponse<AuthResponse>> {
    return this.request<AuthResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    })
  }

  public async logout(): Promise<ApiResponse<void>> {
    const result = await this.request<void>('/auth/logout', {
      method: 'POST',
    })
    this.clearToken()
    return result
  }

  // Events APIs
  public async getEvents(params?: {
    page?: number
    limit?: number
    sport?: string
    status?: string
    districtId?: string
    schoolId?: string
    programId?: string
    /** Only events the caller organizes (server-side). */
    mine?: boolean
    /** Only events whose analytics the caller may read (server-side). */
    followable?: boolean
  }): Promise<ApiResponse<EventsResponse>> {
    const queryParams = new URLSearchParams()

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, String(value))
        }
      })
    }

    const queryString = queryParams.toString()
    const endpoint = queryString ? `/events?${queryString}` : '/events'

    return this.request<EventsResponse>(endpoint)
  }

  public async getEvent(eventId: string): Promise<ApiResponse<Event>> {
    return this.request<Event>(`/events/${eventId}`)
  }

  public async createEvent(eventData: {
    name: string
    description?: string
    sport: 'FOOTBALL' | 'BASEBALL'
    startDate: string
    endDate: string
    location: string
    venue?: string
    maxParticipants?: number
    registrationDeadline?: string
    districtId?: string
    schoolId?: string
    programId?: string
    drillIds: string[]
  }): Promise<ApiResponse<Event>> {
    return this.request<Event>('/events', {
      method: 'POST',
      body: JSON.stringify(eventData),
    })
  }

  public async updateEvent(
    eventId: string,
    eventData: Partial<{
      name: string
      description: string
      startDate: string
      endDate: string
      location: string
      venue: string
      maxParticipants: number
      registrationDeadline: string
      status: 'DRAFT' | 'SCHEDULED' | 'ACTIVE' | 'COMPLETED' | 'CANCELLED'
    }>
  ): Promise<ApiResponse<Event>> {
    return this.request<Event>(`/events/${eventId}`, {
      method: 'PUT',
      body: JSON.stringify(eventData),
    })
  }

  public async deleteEvent(eventId: string): Promise<ApiResponse<void>> {
    return this.request<void>(`/events/${eventId}`, {
      method: 'DELETE',
    })
  }

  public async getEventParticipants(
    eventId: string,
    params?: {
      status?: 'REGISTERED' | 'CHECKED_IN' | 'COMPLETED' | 'NO_SHOW'
    }
  ): Promise<
    ApiResponse<{
      participants: Array<{
        id: string
        status: string
        registeredAt: string
        checkedInAt?: string
        athlete: {
          id: string
          profile: {
            firstName: string
            lastName: string
            position?: string
          }
        }
        team?: {
          id: string
          name: string
        }
        school?: {
          id: string
          name: string
        }
      }>
    }>
  > {
    const queryParams = new URLSearchParams()

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, String(value))
        }
      })
    }

    const queryString = queryParams.toString()
    const endpoint = queryString
      ? `/events/${eventId}/participants?${queryString}`
      : `/events/${eventId}/participants`

    return this.request(endpoint)
  }

  public async checkInParticipant(
    eventId: string,
    participantId: string
  ): Promise<
    ApiResponse<{
      id: string
      status: string
      checkedInAt: string
    }>
  > {
    return this.request(`/events/${eventId}/checkin/${participantId}`, {
      method: 'POST',
    })
  }

  // Organizations APIs
  public async getOrganizationHierarchy(params?: {
    includePrograms?: boolean
    includeTeams?: boolean
    districtId?: string
    schoolId?: string
  }): Promise<ApiResponse<OrganizationHierarchy>> {
    const queryParams = new URLSearchParams()

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, String(value))
        }
      })
    }

    const queryString = queryParams.toString()
    const endpoint = queryString
      ? `/organizations/hierarchy?${queryString}`
      : '/organizations/hierarchy'

    return this.request<OrganizationHierarchy>(endpoint)
  }

  public async getOrganizationNavigation(): Promise<ApiResponse<NavigationResponse>> {
    return this.request<NavigationResponse>('/organizations/navigation')
  }

  // Analytics APIs
  public async getEventAnalytics(eventId: string): Promise<ApiResponse<EventAnalytics>> {
    return this.request<EventAnalytics>(`/analytics/events/${eventId}`)
  }

  public async getOrganizationAnalytics(
    orgType: 'district' | 'school' | 'program',
    orgId: string,
    params?: {
      timeRange?: '30d' | '90d' | '6m' | '1y' | 'all'
      sport?: 'FOOTBALL' | 'BASEBALL'
    }
  ): Promise<
    ApiResponse<{
      organization: {
        id: string
        name: string
        type: string
      }
      summary: {
        totalAthletes: number
        totalEvents: number
        totalResults: number
        averagePerformance: number
        // `participationRate` removed (#159): the backend always sent 85.
      }
      performanceByPosition: Array<{
        position: string
        athleteCount: number
        averagePercentile: number
        topPerformer?: {
          id: string
          name: string
          percentile: number
        }
      }>
      eventHistory: Array<{
        event: {
          id: string
          name: string
          date: string
        }
        participantCount: number
        averagePerformance: number
      }>
    }>
  > {
    const queryParams = new URLSearchParams()

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, String(value))
        }
      })
    }

    const queryString = queryParams.toString()
    const endpoint = queryString
      ? `/analytics/organizations/${orgType}/${orgId}?${queryString}`
      : `/analytics/organizations/${orgType}/${orgId}`

    return this.request(endpoint)
  }

  // Coach Analytics APIs
  public async getCoachOverview(eventId?: string): Promise<ApiResponse<any>> {
    const params = eventId ? `?eventId=${eventId}` : ''
    return this.request(`/analytics/coach/overview${params}`)
  }

  // Parent/Consent APIs
  public async getMyChildren(): Promise<ApiResponse<any>> {
    return this.request('/consent/my-children')
  }

  public async getAthletePerformance(athleteId: string): Promise<ApiResponse<any>> {
    return this.request(`/athletes/${athleteId}/performance`)
  }

  /**
   * `GET /api/athletes/me` — the authenticated caller's own athlete record.
   *
   * Takes no id: the route resolves from the token alone, so there is no
   * parameter through which anyone else's record could be requested. This
   * replaced a client-side heuristic that listed `/api/athletes` narrowed by
   * the user's last name and matched `userId` (#159). A 404 means no athlete
   * profile is linked to the account, which is a legitimate state, not an error.
   */
  public async getMyAthlete(): Promise<ApiResponse<MyAthleteResponse>> {
    return this.request<MyAthleteResponse>('/athletes/me')
  }

  /**
   * `GET /api/analytics/athletes/:athleteId` — the only athlete-analytics route
   * that authorizes correctly for an athlete viewing themselves (it compares
   * `athlete.userId` to the caller's id; see `canViewAthleteAnalytics` in
   * backend/src/routes/analytics.ts). Prefer it over
   * `/athletes/:id/performance`, which declares no response schema and performs
   * no authorization at all.
   */
  public async getAthleteAnalytics(
    athleteId: string,
    params?: { timeRange?: '30d' | '90d' | '6m' | '1y' | 'all'; sport?: 'FOOTBALL' | 'BASEBALL' }
  ): Promise<ApiResponse<AthleteAnalytics>> {
    const queryParams = new URLSearchParams()
    if (params?.timeRange) {
      queryParams.append('timeRange', params.timeRange)
    }
    if (params?.sport) {
      queryParams.append('sport', params.sport)
    }
    const queryString = queryParams.toString()
    return this.request<AthleteAnalytics>(
      `/analytics/athletes/${athleteId}${queryString ? `?${queryString}` : ''}`
    )
  }

  /**
   * `GET /api/monitoring/health`. Reports only what the backend actually
   * probes — a real `SELECT 1` against Postgres and a real Redis `PING`.
   *
   * The handler also computes a `responseTime`, but `HealthStatusSchema` does
   * not declare it, so Fastify strips it off the wire. Do not render an "API
   * response time" from this payload; there isn't one.
   */
  public async getSystemHealth(): Promise<ApiResponse<SystemHealth>> {
    return this.request<SystemHealth>('/monitoring/health')
  }

  // Scopes APIs
  // NOTE: the endpoint returns an `{ scopes: [...] }` envelope, matching the
  // other list endpoints in this API — not a bare array. See
  // backend/src/routes/scopes.ts (`return { scopes }`).
  public async getMyScopes(): Promise<
    ApiResponse<{
      scopes: Array<{
        id: string
        scopeType: string
        scopeId: string | null
        isActive: boolean
      }>
    }>
  > {
    return this.request('/scopes/my-scopes')
  }

  // Programs APIs
  public async getPrograms(params?: {
    page?: number
    limit?: number
    sport?: 'FOOTBALL' | 'BASEBALL'
    level?: 'VARSITY' | 'JV' | 'FRESHMAN' | 'MIDDLE_SCHOOL'
    schoolId?: string
    districtId?: string
    isActive?: boolean
  }): Promise<
    ApiResponse<{
      programs: Program[]
      pagination: {
        page: number
        limit: number
        total: number
        pages: number
      }
    }>
  > {
    const queryParams = new URLSearchParams()

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, String(value))
        }
      })
    }

    const queryString = queryParams.toString()
    const endpoint = queryString ? `/programs?${queryString}` : '/programs'

    return this.request(endpoint)
  }

  // Athletes APIs
  public async getAthletes(params?: {
    page?: number
    limit?: number
    sport?: 'FOOTBALL' | 'BASEBALL'
    position?: string
    search?: string
  }): Promise<
    ApiResponse<{
      athletes: Array<{
        id: string
        userId?: string
        sport?: string
        position?: string
        graduationYear?: number
        // No dateOfBirth / phoneNumber: the list never carries them (#162).
        profile?: {
          firstName?: string
          lastName?: string
        }
        metrics?: { height?: number; weight?: number }
      }>
      pagination?: { page: number; limit: number; total: number; pages: number }
    }>
  > {
    const queryParams = new URLSearchParams()
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, String(value))
        }
      })
    }
    const queryString = queryParams.toString()
    return this.request(queryString ? `/athletes?${queryString}` : '/athletes')
  }

  public async getResults(params?: {
    page?: number
    limit?: number
    eventId?: string
    athleteId?: string
  }): Promise<
    ApiResponse<{
      results: Array<{
        id: string
        value: number
        percentile?: number
        verified: boolean
        notes?: string
        createdAt: string
        athlete: { id: string; profile: { firstName: string; lastName: string } }
        drill: { name: string; unit: string; sport: string }
        event?: { name: string; location: string }
      }>
      pagination?: { page: number; limit: number; total: number; pages: number }
    }>
  > {
    const queryParams = new URLSearchParams()
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, String(value))
        }
      })
    }
    const queryString = queryParams.toString()
    return this.request(queryString ? `/results?${queryString}` : '/results')
  }

  // Public Profile APIs
  /**
   * `GET /api/drills` — the real drill catalogue (backend/src/routes/drills.ts).
   *
   * `Drill.id` is a `cuid()`, so a client cannot synthesise drill ids: anything
   * built from an invented list is either rejected on create or persisted with
   * dangling references. This is the only source of valid ids.
   */
  public async getDrills(params?: {
    page?: number
    limit?: number
    sport?: 'FOOTBALL' | 'BASEBALL'
    category?: string
  }): Promise<ApiResponse<DrillListResponse>> {
    const queryParams = new URLSearchParams()
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, String(value))
        }
      })
    }
    const queryString = queryParams.toString()
    return this.request<DrillListResponse>(queryString ? `/drills?${queryString}` : '/drills')
  }

  public async getPublicProfile(athleteId: string): Promise<ApiResponse<PublicProfileResponse>> {
    return this.request<PublicProfileResponse>(`/profile/${athleteId}`)
  }

  public async getProfileStats(athleteId: string): Promise<ApiResponse<ProfileStatsResponse>> {
    return this.request<ProfileStatsResponse>(`/profile/${athleteId}/stats`)
  }

  // Scout APIs
  public async scoutSearch(params: any): Promise<ApiResponse<any>> {
    const queryParams = new URLSearchParams()
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined) {
        queryParams.append(k, String(v))
      }
    })
    return this.request(`/scout/search?${queryParams}`)
  }

  public async getWatchlist(): Promise<ApiResponse<any>> {
    return this.request('/scout/watchlist')
  }

  public async addToWatchlist(athleteId: string): Promise<ApiResponse<any>> {
    return this.request('/scout/watchlist', { method: 'POST', body: JSON.stringify({ athleteId }) })
  }

  /**
   * `DELETE /api/scout/watchlist/:athleteId`. Without this the UI could only
   * ever add: toggling a star "off" POSTed again, so the star went hollow while
   * the server kept the entry — the page then disagreed with the server about
   * what the scout had saved.
   */
  public async removeFromWatchlist(athleteId: string): Promise<ApiResponse<void>> {
    return this.request<void>(`/scout/watchlist/${athleteId}`, { method: 'DELETE' })
  }

  public async compareAthletes(athleteIds: string[]): Promise<ApiResponse<any>> {
    return this.request(`/scout/compare?athleteIds=${athleteIds.join(',')}`)
  }

  // Command Center APIs
  public async getCommandCenter(eventId: string): Promise<ApiResponse<any>> {
    return this.request(`/command-center/event/${eventId}`)
  }

  public async getCommandCenterStations(eventId: string): Promise<ApiResponse<any>> {
    return this.request(`/command-center/event/${eventId}/stations`)
  }

  public async validateResult(
    eventId: string,
    resultId: string,
    action: string,
    reason?: string
  ): Promise<ApiResponse<any>> {
    return this.request(`/command-center/event/${eventId}/validate/${resultId}`, {
      method: 'POST',
      body: JSON.stringify({ action, reason }),
    })
  }

  public async getTeams(params?: {
    page?: number
    limit?: number
    programId?: string
    schoolId?: string
    level?: 'VARSITY' | 'JV' | 'FRESHMAN' | 'MIDDLE_SCHOOL'
    isActive?: boolean
  }): Promise<
    ApiResponse<{
      teams: Team[]
      pagination: {
        page: number
        limit: number
        total: number
        pages: number
      }
    }>
  > {
    const queryParams = new URLSearchParams()

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, String(value))
        }
      })
    }

    const queryString = queryParams.toString()
    const endpoint = queryString ? `/teams?${queryString}` : '/teams'

    return this.request(endpoint)
  }

  // Video/Highlight APIs
  public async uploadClip(formData: FormData): Promise<ApiResponse<any>> {
    if (isDemoMode()) {
      return { data: { id: `clip-${Date.now()}`, message: 'Upload simulated in demo mode' } }
    }
    try {
      const response = await fetch(`${API_BASE_URL}/video/upload`, {
        method: 'POST',
        headers: {
          ...(this.token && { Authorization: `Bearer ${this.token}` }),
        },
        body: formData,
      })

      const data = await response.json()

      if (!response.ok) {
        return { error: data.error || 'Upload failed', message: data.message }
      }

      return { data }
    } catch (_error) {
      return { error: 'Network Error', message: 'Failed to upload clip' }
    }
  }

  public async getAthleteClips(athleteId: string): Promise<ApiResponse<{ clips: any[] }>> {
    return this.request(`/video/athlete/${athleteId}`)
  }

  public async getVideoTags(
    sport: string
  ): Promise<ApiResponse<{ sport: string; tags: string[] }>> {
    return this.request(`/video/tags/${sport}`)
  }

  public async deleteClip(clipId: string): Promise<ApiResponse<void>> {
    return this.request(`/video/clip/${clipId}`, { method: 'DELETE' })
  }

  public async updateClip(
    clipId: string,
    data: { title?: string; description?: string; skillTag?: string }
  ): Promise<ApiResponse<any>> {
    return this.request(`/video/clip/${clipId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  public async approveClip(clipId: string): Promise<ApiResponse<any>> {
    return this.request(`/video/clip/${clipId}/approve`, { method: 'POST' })
  }

  // Training Plan APIs
  public async generateTrainingPlan(data: {
    athleteId: string
    planType: 'performance' | 'rehabilitation' | 'custom'
    goals?: string
    injuryType?: string
  }): Promise<ApiResponse<any>> {
    return this.request('/training/generate', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  public async getTrainingPlans(athleteId: string): Promise<ApiResponse<{ plans: any[] }>> {
    return this.request(`/training/plans/${athleteId}`)
  }

  public async getTrainingPlanDetail(planId: string): Promise<ApiResponse<any>> {
    return this.request(`/training/plans/${planId}/detail`)
  }

  public async approveTrainingPlan(planId: string): Promise<ApiResponse<any>> {
    return this.request(`/training/plans/${planId}/approve`, { method: 'POST' })
  }

  public async exportTrainingPlan(planId: string): Promise<ApiResponse<any>> {
    return this.request(`/training/plans/${planId}/export`)
  }
}

export const apiService = ApiService.getInstance()

export default apiService
